/*
  Sticky columns on the product page.

  Two columns of different heights leave the short one finished while the tall
  one is still going — the empty space under the gallery. Whichever column is
  shorter is pinned instead, so both sides stay in view to the end.

  Which one that is cannot be decided in the theme: it depends on the product.
  A long description with cross-sells makes the info column taller; six images
  and a thumbnail rail make the media column taller. So it is measured, and
  re-measured whenever the heights change.

  Dawn ships `enable_sticky_info`, which pins the info column only. That is the
  wrong half whenever the info column is the tall one, which is the common case
  on this store — and pinning a column taller than the window hides its own
  bottom, because a sticky box stops at its top offset and its lower content
  never comes back up.

  The taller column is left alone. It has to scroll; that is what makes the
  page long in the first place.
*/
(function stickyColumns() {
  const DESKTOP = '(min-width: 990px)';
  // Below this the columns are close enough that pinning buys nothing and only
  // makes the page feel like it is holding still.
  const MIN_DIFFERENCE = 60;

  function measure(section) {
    const media = section.querySelector('.product__media-wrapper');
    const info = section.querySelector('.product__info-wrapper');
    if (!media || !info) return null;
    return { media, info };
  }

  /*
    The CONTENT height of each column, which is not the height of the wrapper.

    Both wrappers are grid items, and a grid item stretches to its row by
    default — so both reported 1287px, exactly equal, and the comparison below
    concluded there was nothing to pin. Measuring the box measured the row.

    The inner element is the real content: the gallery on one side, Dawn's
    info container on the other. The wrapper's own vertical padding is added
    back, because that is height the column really occupies.
  */
  function contentHeight(wrapper, inner) {
    if (!inner) return wrapper.offsetHeight;
    const styles = getComputedStyle(wrapper);
    const padding = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
    return inner.getBoundingClientRect().height + (padding || 0);
  }

  function heights(section, parts) {
    const gallery = parts.media.querySelector('media-gallery') || parts.media.firstElementChild;
    const details = parts.info.querySelector('.product__info-container') || parts.info.firstElementChild;
    return {
      media: contentHeight(parts.media, gallery),
      info: contentHeight(parts.info, details),
    };
  }

  function clear(parts) {
    [parts.media, parts.info].forEach((column) => {
      column.classList.remove('tyo-sticky');
      column.style.removeProperty('--tyo-sticky-offset');
    });
  }

  function apply(section) {
    const parts = measure(section);
    if (!parts) return;

    // Cleared before measuring: a pinned column reports the same height, but
    // starting from a known state keeps a re-run from compounding.
    clear(parts);

    if (!window.matchMedia(DESKTOP).matches) return;

    const { media: mediaHeight, info: infoHeight } = heights(section, parts);
    if (Math.abs(mediaHeight - infoHeight) < MIN_DIFFERENCE) return;

    const short = mediaHeight < infoHeight ? parts.media : parts.info;
    const shortHeight = Math.min(mediaHeight, infoHeight);

    const styles = getComputedStyle(section);
    const gap = parseInt(styles.getPropertyValue('--tyo-sticky-top'), 10) || 30;

    /*
      A column taller than the window cannot simply be pinned to the top — its
      own bottom would never be reachable. Pinning it by its bottom edge
      instead lets the rest of it come up as the page scrolls, and it settles
      once its end is on screen.
    */
    const room = window.innerHeight - gap;
    const offset = shortHeight > room ? window.innerHeight - shortHeight - gap : gap;

    short.style.setProperty('--tyo-sticky-offset', `${Math.round(offset)}px`);
    short.classList.add('tyo-sticky');
  }

  function watch(section) {
    if (section.dataset.tyoSticky) return;
    section.dataset.tyoSticky = 'true';

    let frame = null;
    const schedule = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => apply(section));
    };

    schedule();
    window.addEventListener('resize', schedule);

    /*
      Heights settle late and keep moving: images load, the variant picker
      swaps content, the cross-sell rows arrive. A ResizeObserver on both
      columns catches all of it without a timer.

      It observes the columns, not the section — the section's height is a
      consequence of the pinning, so watching it would feed back on itself.
    */
    const parts = measure(section);
    if (parts && 'ResizeObserver' in window) {
      const observer = new ResizeObserver(schedule);
      /*
        The inner elements, for the same reason they are what gets measured: a
        stretched wrapper keeps the row's height whatever happens inside it, so
        watching the wrappers would never fire when the content actually grew.
      */
      const gallery = parts.media.querySelector('media-gallery') || parts.media.firstElementChild;
      const details = parts.info.querySelector('.product__info-container') || parts.info.firstElementChild;
      if (gallery) observer.observe(gallery);
      if (details) observer.observe(details);
    }
  }

  function start() {
    document.querySelectorAll('[id^="MainProduct-"]').forEach(watch);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', start);
})();
